/**
 * Shared button styles to avoid duplication across components
 * These match the design standards in DESIGN_STANDARDS.md
 */

import { SxProps, Theme } from "@mui/material";

/**
 * Transparent cancel/secondary button style
 * Used in: dialogs, forms, modals
 */
export const cancelButtonStyle: SxProps<Theme> = {
  textTransform: "none",
  color: "text.primary",
  px: 0,
  minWidth: "auto",
  "&:hover": {
    backgroundColor: "transparent",
    opacity: 0.7,
  },
  "&:active": {
    backgroundColor: "transparent",
    opacity: 0.5,
  },
};

/**
 * Primary action button style
 * Used in: forms, dialogs (submit actions)
 */
export const primaryButtonStyle: SxProps<Theme> = {
  textTransform: "none",
  color: "text.primary",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "transparent",
  },
  "&:disabled": {
    color: "text.disabled",
    backgroundColor: "transparent",
  },
};

/**
 * Icon button style (secondary/muted)
 * Used in: cards, toolbars
 */
export const iconButtonStyle: SxProps<Theme> = {
  color: "text.secondary",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "transparent",
    opacity: 0.7,
  },
  "&:active": {
    backgroundColor: "transparent",
    opacity: 0.5,
  },
};

/**
 * Icon button style (danger/delete)
 * Used in: delete actions, destructive operations
 */
export const dangerIconButtonStyle: SxProps<Theme> = {
  color: "error.main",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "transparent",
    opacity: 0.7,
  },
  "&:active": {
    backgroundColor: "transparent",
    opacity: 0.5,
  },
};

/**
 * Icon button with underline hover (alternate pattern)
 * Used in: image upload modal, expandable sections
 */
export const iconButtonUnderlineStyle: SxProps<Theme> = {
  color: "text.secondary",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  textDecoration: "none",
  "&:hover": {
    color: "text.primary",
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
  "&:active": {
    backgroundColor: "transparent",
    opacity: 0.7,
  },
};

/**
 * Text button with simple underline hover (no color change)
 * Used in: file browse buttons
 */
export const textButtonUnderlineStyle: SxProps<Theme> = {
  textTransform: "none",
  color: "text.primary",
  px: 0,
  minWidth: "auto",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
  "&:active": {
    backgroundColor: "transparent",
    opacity: 0.7,
  },
};

/**
 * Danger icon button with underline hover
 * Used in: image upload modal delete buttons
 */
export const dangerIconButtonUnderlineStyle: SxProps<Theme> = {
  color: "text.secondary",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  textDecoration: "none",
  "&:hover": {
    color: "error.main",
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
  "&:active": {
    backgroundColor: "transparent",
    opacity: 0.7,
  },
};

/**
 * Navigation-style button with animated underline
 * Used in: category/subcategory cards, navigation-like buttons
 */
export const navStyleButtonStyle: SxProps<Theme> = {
  color: "text.secondary",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  textDecoration: "none",
  position: "relative",
  transition: (theme) => `color ${theme.custom.transitions.fast}`,
  "&:hover": {
    color: "text.primary",
    backgroundColor: "transparent",
    "&::after": {
      width: "100%",
    },
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-2px",
    left: 0,
    width: "0%",
    height: "1px",
    backgroundColor: "text.primary",
    transition: (theme) => `width ${theme.custom.transitions.standard}`,
  },
};

/**
 * Navigation-style danger button with animated underline
 * Used in: category cards delete buttons
 */
export const navStyleDangerButtonStyle: SxProps<Theme> = {
  color: "error.main",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  textDecoration: "none",
  position: "relative",
  transition: (theme) => `color ${theme.custom.transitions.fast}`,
  "&:hover": {
    color: "error.main",
    backgroundColor: "transparent",
    "&::after": {
      width: "100%",
    },
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-2px",
    left: 0,
    width: "0%",
    height: "1px",
    backgroundColor: "error.main",
    transition: (theme) => `width ${theme.custom.transitions.standard}`,
  },
};

/**
 * Button with subtle background hover (alternate pattern for subcategory cards)
 * Used in: subcategory cards edit buttons
 */
export const subtleHoverButtonStyle: SxProps<Theme> = {
  color: "text.secondary",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  "&:hover": {
    color: "text.primary",
    backgroundColor: "transparent",
  },
};

/**
 * Danger button with subtle background hover
 * Used in: subcategory cards delete buttons
 */
export const subtleHoverDangerButtonStyle: SxProps<Theme> = {
  color: "error.main",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "transparent",
  },
};

/**
 * Toolbar/action bar button style
 * Used in: BulkActionsBar, Pagination, other toolbars
 * Minimal styling with subtle hover feedback
 */
export const toolbarButtonStyle: SxProps<Theme> = {
  textTransform: "none",
  color: "text.primary",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  fontSize: "0.875rem",
  fontWeight: 400,
  "&:hover": {
    backgroundColor: "transparent",
    opacity: 0.9,
  },
  "&:active": {
    backgroundColor: "transparent",
    opacity: 0.8,
  },
  "&:disabled": {
    color: "text.disabled",
    backgroundColor: "transparent",
  },
};

/**
 * Toolbar danger button style
 * Used in: BulkActionsBar delete actions
 */
export const toolbarDangerButtonStyle: SxProps<Theme> = {
  textTransform: "none",
  color: "error.main",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  fontSize: "0.875rem",
  fontWeight: 400,
  "&:hover": {
    backgroundColor: "transparent",
    opacity: 0.9,
  },
  "&:active": {
    backgroundColor: "transparent",
    opacity: 0.8,
  },
  "&:disabled": {
    color: "text.disabled",
    backgroundColor: "transparent",
  },
};
